/**
 * "Safe to invest" — how much of THIS MONTH's surplus may go into accumulating assets
 * (spec v2 §9 `FREE_CASH`, decision AC1 2026-09-11; replaces the balance-sheet formula of
 * step 1.7 / S2).
 *
 * The owner's Excel has a row "Còn lại": salary minus every monthly outflow — rent, bills,
 * insurance, the reserve contribution, the existing DCA. That leftover is what may be added
 * to investing. The ledger computes the same row as `money_flow.allocation.free_cash_stable`
 * (DBD-023); this module takes it and removes one thing the Excel never had: a margin for the
 * spending that arrives unannounced, measured from the household's own history.
 *
 * What it deliberately does NOT subtract: the emergency fund's shortfall. Nobody fills a
 * 240m fund in a month; it is built by a monthly contribution that is already inside
 * `free_cash_stable` as the RESERVE bucket. Taking the whole gap out of a monthly figure read
 * 0 for years and made every agent refuse to discuss investing (AC1). The fund's pace is a
 * separate question — `computeReserveTargets` below.
 *
 * Pure. Every input arrives already summed by SQL (`app.investable_capacity_inputs`, DBD-014);
 * nothing here adds two amounts of money that came from different rows.
 */
/** Trailing window the risk margin is averaged over. Mirrors `.agents/rules/finance-analysis.md` §9. */
export const RISK_MARGIN_WINDOW_MONTHS = 6;
/** Reserve targets in months of spending (or of stable income when spending is unknown) — §9. */
export const RESERVE_BASIC_MONTHS = 6;
export const RESERVE_SAFE_MONTHS = 18;
export function computeSafeToInvest(input) {
    const historyMonths = Math.max(0, Math.floor(input.historyMonths));
    const riskMargin = historyMonths === 0 ? 0 : Math.round(input.unexpectedTotal / historyMonths);
    const afterMargin = Math.max(0, input.freeCashStable - riskMargin);
    const headroom = input.liquidBalances - input.upcomingObligations - input.minCashBuffer;
    const floorTopUp = headroom < 0 ? Math.min(afterMargin, -headroom) : 0;
    return {
        amount: afterMargin - floorTopUp,
        breakdown: {
            freeCashStable: input.freeCashStable,
            riskMargin,
            historyMonths,
            windowMonths: RISK_MARGIN_WINDOW_MONTHS,
            basis: historyMonths === 0
                ? 'none'
                : historyMonths < RISK_MARGIN_WINDOW_MONTHS
                    ? 'partial'
                    : 'full',
            afterMargin,
            floorTopUp,
            shortfall: input.freeCashStable < 0 ? input.freeCashStable : 0,
        },
        liquidity: {
            liquidBalances: input.liquidBalances,
            upcomingObligations: input.upcomingObligations,
            minCashBuffer: input.minCashBuffer,
            headroom,
            belowFloor: headroom < 0,
        },
    };
}
/** Months of `contribution` needed to lift `balance` up to `target`. */
export function monthsToTarget(target, balance, contribution) {
    if (target === null)
        return null;
    if (target <= balance)
        return 0;
    if (contribution <= 0)
        return null;
    return Math.ceil((target - balance) / contribution);
}
/**
 * The emergency fund as a PACE, not a gap (AC2): six months of spending is the basic target,
 * eighteen the safe one, and "how many months of contributions until then" is what a person
 * can act on. Spending unknown ⇒ fall back to stable income, on the assumption that a salary
 * covers a month — the caller must say which basis was used.
 */
export function computeReserveTargets(input) {
    const basis = input.monthlyExpense !== null && input.monthlyExpense > 0
        ? 'expense'
        : input.stableIncome !== null && input.stableIncome > 0
            ? 'income'
            : null;
    const basisAmount = basis === 'expense' ? input.monthlyExpense : basis === 'income' ? input.stableIncome : null;
    const basicTarget = basisAmount === null ? null : basisAmount * RESERVE_BASIC_MONTHS;
    const safeTarget = basisAmount === null ? null : basisAmount * RESERVE_SAFE_MONTHS;
    return {
        basis,
        basisAmount,
        basicTarget,
        safeTarget,
        monthsToBasic: monthsToTarget(basicTarget, input.balance, input.monthlyContribution),
        monthsToSafe: monthsToTarget(safeTarget, input.balance, input.monthlyContribution),
    };
}
/**
 * How far the reserves still are from their targets, in total. Kept for callers that want to
 * SAY the gap; since AC1 it is no longer subtracted from anything.
 *
 * A reserve that is over its target contributes nothing — money above the target is not a
 * gap, and letting it go negative would quietly let one over-funded reserve pay for another
 * one's shortfall.
 */
export function computeReserveGap(policies) {
    return policies.reduce((total, policy) => {
        if (policy.targetAmount === null)
            return total;
        return total + Math.max(0, policy.targetAmount - policy.balance);
    }, 0);
}
//# sourceMappingURL=safeToInvest.js.map